import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  deriveStatus,
  getCardsByPackAndLanguage,
  getSession,
  putSession,
  setPackProgress,
} from "../db";
import type { Card, Session } from "../domain/types";
import type { ActionResult } from "../domain/enums";
import { applyAction, undoLastAction } from "../engine/session";
import { useLanguage } from "../state/language";
import { Button } from "../components/Button";
import { Modal } from "../components/Modal";
import { PhaseFlash } from "../components/PhaseFlash";
import { isDebugEnabled } from "../lib/debug";
import { useWakeLock } from "../lib/wakeLock";
import { PickingAnimation } from "../components/PickingAnimation";
import { ReactionStage } from "../components/ReactionStage";
import { ConsequenceStage } from "../components/ConsequenceStage";
import { MiniChallengeTimer } from "../components/MiniChallengeTimer";
import { CARD_TYPE_ICON, CARD_TYPE_TINT, modeStyle } from "../components/modeTheme";
import { SoftTimer } from "../components/SoftTimer";
import { cardTypeName, phaseName } from "../domain/i18n";
import type { StringKey } from "../domain/i18n";

type Stage = "phase_flash" | "picking" | "card" | "consequence" | "reaction";

export default function SessionScreen() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const nav = useNavigate();
  const { lang, t } = useLanguage();
  const language = lang ?? "uk";

  const [sess, setSess] = useState<Session | null>(null);
  const [packCards, setPackCards] = useState<Card[]>([]);
  // Party in progress = screen stays on.
  useWakeLock(sess != null);
  const [stage, setStage] = useState<Stage>("phase_flash");
  const [busy, setBusy] = useState(false);
  /** Which outcome the card is currently animating out with (≤280ms). */
  const [exitAction, setExitAction] = useState<ActionResult | null>(null);
  const [closeOpen, setCloseOpen] = useState(false);
  /** State carried into the reaction stage so we know what to do next. */
  const [postReaction, setPostReaction] = useState<{
    nextStage: Stage;
    durationSec: number;
  } | null>(null);
  /** Reward / penalty payload we hold while the consequence stage is on-screen. */
  const [pendingConsequence, setPendingConsequence] = useState<{
    kind: "reward" | "penalty";
    text: string;
    playerName: string;
    /** Card id of the consequence — passed to ConsequenceStage for the
     *  debug overlay so the host can screenshot and the editor knows
     *  exactly which CSV row to fix. */
    cardId: string;
    /** Players engine picked to ACT on the spotlight (e.g. "Алина & Саша
     *  do 3 hand-claps for Vika"). Empty if consequence card is solo. */
    helperNames: string[];
    /** When the consequence card has its own timer (e.g. mini_challenge or
     *  any card with durationSec set), the consequence stage runs as a
     *  hard-timed challenge — auto-advance on zero. When null/undefined,
     *  host taps "Далі" manually. */
    timerDurationSec: number | null;
    nextStage: Stage;
    nextReaction?: { durationSec: number; nextStage: Stage };
  } | null>(null);

  // Load session + pack cards on mount / id change.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      const s = (await getSession(sessionId)) ?? null;
      if (cancelled) return;
      if (!s) {
        nav("/modes", { replace: true });
        return;
      }
      setSess(s);
      const cards = await getCardsByPackAndLanguage(s.packId, s.language);
      if (cancelled) return;
      setPackCards(cards);
      // §3.4: always show phase flash on the first card of a session.
      setStage(s.cardsSeen === 0 ? "phase_flash" : "card");
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, nav]);

  // If language changes mid-session, swap card content (§7).
  useEffect(() => {
    if (!sess) return;
    if (sess.language === language) return;
    (async () => {
      const cards = await getCardsByPackAndLanguage(sess.packId, language);
      setPackCards(cards);
      // Note: we do NOT regenerate the queue order — the session keeps its seed.
      // We just look up cards by id in the new language for display.
    })();
  }, [language, sess]);

  const cardLookupByLang = useMemo(() => {
    // Cards we hold are language-filtered; we look up by id which is per-language anyway.
    const m = new Map<string, Card>();
    for (const c of packCards) m.set(c.id, c);
    return m;
  }, [packCards]);

  const currentCard: Card | undefined = sess?.currentCardId
    ? cardLookupByLang.get(sess.currentCardId) ??
      // The queue uses uk-language ids if Players started in uk; if user switched
      // languages mid-session and that pack lacks the same id, fall back to first
      // card of same-pack same-language.
      packCards[sess.cardsSeen]
    : undefined;

  // Public skips (game-theory fix): skip is a hot-potato handoff — the same
  // card passes to the next player. Surface WHO passed it so skipping is a
  // visible social move, not a silent escape. Walks the event tail collecting
  // consecutive skips of the current card, oldest first.
  const skipChain = useMemo(() => {
    if (!sess?.currentCardId) return [];
    const names: string[] = [];
    for (let i = sess.events.length - 1; i >= 0; i--) {
      const e = sess.events[i]!;
      // Replace keeps the same player and swaps the card — irrelevant here.
      if (e.action === "replaced") continue;
      if (e.action !== "skipped" || e.cardId !== sess.currentCardId) break;
      const nm = sess.players.find((p) => p.id === e.playerId)?.name;
      if (nm) names.unshift(nm);
    }
    return names;
  }, [sess]);

  const onUndo = useCallback(async () => {
    if (!sess || busy) return;
    const restored = undoLastAction(sess);
    if (!restored) return;
    setBusy(true);
    await putSession(restored);
    const seen = restored.cardsSeen;
    const total = restored.totalCards;
    await setPackProgress({
      packId: restored.packId,
      status: deriveStatus(seen, total),
      progressPct: total > 0 ? Math.round((seen / total) * 100) : 0,
      cardsSeen: seen,
      totalCards: total,
      lastSessionId: restored.id,
      updatedAt: Date.now(),
    });
    setSess(restored);
    setStage("card"); // After undo, we're back on the previous card.
    setBusy(false);
  }, [sess, busy]);

  const onPick = useCallback(
    async (action: ActionResult) => {
      if (!sess || !currentCard || busy) return;
      setBusy(true);
      // Outcome-aware exit: the card visibly reacts to the verdict before the
      // flow moves on. Buttons are already disabled via `busy`. The class is
      // cleared only at the END of this handler so the hidden card can't
      // flash back during the async DB writes below (`forwards` fill keeps
      // it at opacity 0 until stage + card advance commit together).
      setExitAction(action);
      await new Promise((r) => setTimeout(r, 280));
      const result = applyAction(sess, action, packCards);
      const next = result.next;
      // Persist (§6: write after every action).
      await putSession(next);
      const seen = next.cardsSeen;
      const total = next.totalCards;
      await setPackProgress({
        packId: next.packId,
        status: deriveStatus(seen, total),
        progressPct: total > 0 ? Math.round((seen / total) * 100) : 0,
        cardsSeen: seen,
        totalCards: total,
        lastSessionId: next.id,
        updatedAt: Date.now(),
      });
      setSess(next);

      if (result.sessionEnded) {
        nav(`/stats/${next.id}`, { replace: true });
        return;
      }
      // Compute reaction window from the card we JUST consumed (not the next one).
      const justPlayed = packCards.find((c) => c.id === sess.currentCardId);
      const reactionSec =
        (action === "done" || action === "failed") &&
        justPlayed?.reactionWindowSec &&
        justPlayed.reactionWindowSec > 0
          ? Math.min(15, Math.max(2, justPlayed.reactionWindowSec))
          : 0;
      const baseNextStage: Stage = result.phaseChanged ? "phase_flash" : "picking";

      if (action === "replaced") {
        setStage("card");
      } else if (result.consequence) {
        // Show reward / penalty first; then optionally reaction; then next pick.
        const playerName =
          sess.players.find((p) => p.id === sess.currentPlayerId)?.name ?? "";
        // Map helper IDs (engine picked) to display names.
        const helperNames = (result.consequence.helperIds ?? [])
          .map((id) => sess.players.find((p) => p.id === id)?.name ?? "")
          .filter(Boolean);
        // Look up the consequence card in packCards so we can honour its own
        // timer / hasTimer settings — penalty-as-challenge runs on its own
        // duration, penalty-as-statement runs on host tap.
        const consequenceCard = packCards.find(
          (c) => c.id === result.consequence!.cardId
        );
        // Match the author's mental model: ANY timer set on the consequence
        // card means "run a hero countdown for this many seconds". Prefer
        // durationSec (the canonical mini-challenge timer), fall back to
        // answerTimeSec (the soft prompt timer that authors often set on
        // task/question penalties — like "50 sec to keep tongue out"). If
        // neither is set, the stage runs in manual "Далі →" mode.
        const timerDurationSec =
          consequenceCard?.durationSec ??
          consequenceCard?.answerTimeSec ??
          null;
        setPendingConsequence({
          kind: result.consequence.kind,
          text: result.consequence.text,
          playerName,
          cardId: result.consequence.cardId,
          helperNames,
          timerDurationSec,
          nextStage: baseNextStage,
          nextReaction:
            reactionSec > 0
              ? { durationSec: reactionSec, nextStage: baseNextStage }
              : undefined,
        });
        setStage("consequence");
      } else if (reactionSec > 0) {
        setPostReaction({ nextStage: baseNextStage, durationSec: reactionSec });
        setStage("reaction");
      } else if (result.phaseChanged) {
        setStage("phase_flash");
      } else {
        setStage("picking");
      }
      // Batched with the stage/session updates above — the exit class comes
      // off in the same commit that swaps the screen, so no flash-back.
      setExitAction(null);
      setBusy(false);
    },
    [sess, currentCard, busy, packCards, nav]
  );

  if (!sess) return <div className="pp-screen items-center justify-center">{t("loading")}</div>;

  return (
    <div className="pp-screen px-5 relative" style={modeStyle(sess.modeId)}>
      <header className="pt-3 z-20 relative">
        <div className="flex items-center justify-between gap-2">
          <PhaseBadge phase={sess.currentPhase} language={language} />
          <div className="flex items-center gap-2 shrink-0">
            {sess.undoSnapshot && (
              <button
                aria-label={t("cta_undo")}
                title={t("cta_undo")}
                className="rounded-full px-3 py-2 bg-white/10 hover:bg-white/20 active:scale-95 text-sm transition disabled:opacity-50"
                onClick={onUndo}
                disabled={busy}
              >
                ↶
              </button>
            )}
            <button
              aria-label={t("cta_close")}
              className="rounded-full p-2 bg-white/10 active:scale-95 disabled:opacity-50"
              onClick={() => setCloseOpen(true)}
              disabled={busy}
            >
              ✕
            </button>
          </div>
        </div>
        <div className="flex items-end gap-3 mt-2">
          <div className="font-display font-black text-3xl tabular-nums leading-none">
            {/* key → re-mount on change so the pop replays per card. */}
            <span key={sess.cardsSeen} className="pp-num-pop">
              {sess.cardsSeen}
            </span>
            <span className="opacity-40 text-xl">/{sess.totalCards}</span>
          </div>
          <ProgressBar value={sess.cardsSeen / Math.max(1, sess.totalCards)} />
        </div>
        <PicksTicker sess={sess} />
      </header>

      {stage === "phase_flash" && (
        <PhaseFlash
          phase={sess.currentPhase}
          language={language}
          onDone={() => setStage("picking")}
        />
      )}

      {stage === "consequence" && pendingConsequence && (
        <ConsequenceStage
          kind={pendingConsequence.kind}
          text={pendingConsequence.text}
          playerName={pendingConsequence.playerName}
          cardId={pendingConsequence.cardId}
          helperNames={pendingConsequence.helperNames}
          timerDurationSec={pendingConsequence.timerDurationSec}
          onDone={() => {
            const after = pendingConsequence;
            setPendingConsequence(null);
            if (after.nextReaction) {
              setPostReaction({
                durationSec: after.nextReaction.durationSec,
                nextStage: after.nextReaction.nextStage,
              });
              setStage("reaction");
            } else {
              setStage(after.nextStage);
            }
          }}
        />
      )}

      {stage === "reaction" && postReaction && (
        <ReactionStage
          durationSec={postReaction.durationSec}
          onDone={() => {
            const next = postReaction.nextStage;
            setPostReaction(null);
            setStage(next);
          }}
        />
      )}

      {stage === "picking" && (
        <PickingAnimation
          players={sess.players}
          finalId={sess.currentPlayerId ?? sess.players[0]!.id}
          onSettled={() => setStage("card")}
        />
      )}

      {stage === "card" && currentCard && (
        <div className="flex-1 flex flex-col py-4">
          {/* Everything above the action pad animates out together on verdict. */}
          <div
            className={`flex-1 flex flex-col ${
              exitAction ? `pp-exit-${exitAction}` : ""
            }`}
          >
          {/* key → remount per card so the stagger reveal replays. */}
          <div key={`hdr-${currentCard.id}`} className="text-center flex flex-col items-center">
            <div
              className="pp-fade-up inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider border"
              style={{
                color: CARD_TYPE_TINT[currentCard.type],
                borderColor: `${CARD_TYPE_TINT[currentCard.type]}66`,
                background: `${CARD_TYPE_TINT[currentCard.type]}1a`,
              }}
            >
              <span aria-hidden className="text-base leading-none">
                {CARD_TYPE_ICON[currentCard.type]}
              </span>
              <span>{cardTypeName(language, currentCard.type)}</span>
            </div>
            {currentCard.interactionMode && currentCard.interactionMode !== "self" && (
              <div
                className="pp-fade-up mt-2 text-[11px] uppercase tracking-wider opacity-70 max-w-xs"
                style={{ animationDelay: "50ms" }}
              >
                {t(`mode_${currentCard.interactionMode}` as StringKey)}
              </div>
            )}
            {skipChain.length > 0 && (
              /* Hot-potato badge — the room sees who passed this card. */
              <div
                className="pp-fade-up mt-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider bg-amber-400/15 border border-amber-300/40 text-amber-200 max-w-[320px] truncate"
                style={{ animationDelay: "70ms" }}
              >
                <span aria-hidden>↷</span>
                <span className="truncate">Пропуск: {skipChain.join(" → ")}</span>
              </div>
            )}
            <div
              className="pp-fade-up font-display text-4xl sm:text-5xl font-black uppercase tracking-tight mt-2 leading-none"
              style={{
                color: "#fff",
                textShadow:
                  "0 0 18px var(--accent, rgba(255,255,255,0.55)), 0 0 36px rgba(0,0,0,0.4)",
                animationDelay: "90ms",
              }}
            >
              {sess.players.find((p) => p.id === sess.currentPlayerId)?.name}
            </div>
            {sess.currentHelperIds && sess.currentHelperIds.length > 0 && (
              <div className="mt-2 flex flex-col items-center gap-1 max-w-[300px]">
                <span className="text-[10px] uppercase tracking-[0.18em] opacity-65 font-bold">
                  {(() => {
                    // Semantic label so guests instantly know who these
                    // people are: vote/group → "the rest of the room",
                    // pair → "partner", ask → "helper", default → "with".
                    const t = currentCard.type;
                    const pc = currentCard.participantsCount;
                    if (pc === "all" || t === "vote" || t === "group")
                      return "Уся компанія";
                    if (t === "pair") return "Пара";
                    if (currentCard.interactionMode === "ask") return "Помічник";
                    return sess.currentHelperIds.length === 1
                      ? "Помічник"
                      : "Помічники";
                  })()}
                </span>
                <div className="flex flex-wrap items-center justify-center gap-1.5">
                {sess.currentHelperIds.map((hid) => {
                  const helper = sess.players.find((p) => p.id === hid);
                  if (!helper) return null;
                  return (
                    <span
                      key={hid}
                      className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-2.5 py-0.5 text-xs font-semibold"
                    >
                      <span
                        className="w-4 h-4 rounded-full bg-white/20 flex items-center justify-center text-[10px]"
                        aria-hidden
                      >
                        {helper.name[0]?.toUpperCase()}
                      </span>
                      {helper.name}
                    </span>
                  );
                })}
                </div>
              </div>
            )}
          </div>
          <div className="flex-1 flex flex-col items-center justify-center mt-4 mb-2 gap-5">
            {currentCard.type === "mini_challenge" && currentCard.durationSec ? (
              <div className="flex flex-col items-center gap-4">
                <MiniChallengeTimer
                  cardId={currentCard.id}
                  durationSec={currentCard.durationSec}
                />
                <p className="text-center text-lg font-medium px-4 max-w-md opacity-90">
                  {currentCard.text}
                </p>
                <p className="text-xs opacity-60 text-center">
                  {t("timer_tap_to_start")} · {t("timer_long_press_reset")}
                </p>
              </div>
            ) : (
              <>
                {currentCard.answerTimeSec && currentCard.answerTimeSec > 0 && (
                  <SoftTimer
                    cardKey={currentCard.id}
                    durationSec={currentCard.answerTimeSec}
                  />
                )}
                <div
                  key={currentCard.id}
                  className={`pp-card-hero w-full max-w-md text-center font-display font-bold leading-snug text-balance ${
                    currentCard.text.length > 140
                      ? "text-lg"
                      : currentCard.text.length > 80
                        ? "text-xl"
                        : currentCard.text.length > 50
                          ? "text-2xl"
                          : "text-3xl"
                  }`}
                >
                  {currentCard.text}
                </div>
                {isDebugEnabled() && (
                  <div className="text-[10px] font-mono opacity-50 px-2 py-0.5 rounded bg-black/30 border border-white/10 select-text">
                    🛠 {currentCard.id}
                  </div>
                )}
              </>
            )}
          </div>
          </div>

          {/* Action pad — colour-coded so the host reads it peripherally at
              arm's length: green = done, rose = failed, neutrals below.
              Primary outcomes get the bigger row. */}
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => onPick("done")}
              disabled={busy}
              className="rounded-2xl px-4 py-4 font-display font-black text-lg uppercase tracking-tight bg-emerald-400 text-emerald-950 active:scale-95 transition disabled:opacity-40"
              style={{ boxShadow: "0 0 22px rgba(52,211,153,0.45)" }}
            >
              <span aria-hidden>✓ </span>
              {t("cta_done")}
            </button>
            <button
              type="button"
              onClick={() => onPick("failed")}
              disabled={busy}
              className="rounded-2xl px-4 py-4 font-display font-black text-lg uppercase tracking-tight bg-rose-500/15 border border-rose-400/50 text-rose-200 active:scale-95 transition disabled:opacity-40"
            >
              <span aria-hidden>✗ </span>
              {t("cta_failed")}
            </button>
            <button
              type="button"
              onClick={() => onPick("skipped")}
              disabled={busy}
              className="rounded-xl px-4 py-3 text-sm font-semibold border border-white/25 bg-white/5 text-white/85 active:scale-95 transition disabled:opacity-40"
            >
              <span aria-hidden>↷ </span>
              {t("cta_skip")}
            </button>
            <button
              type="button"
              onClick={() => onPick("replaced")}
              disabled={busy}
              className="rounded-xl px-4 py-3 text-sm font-semibold border border-white/25 bg-white/5 text-white/85 active:scale-95 transition disabled:opacity-40"
            >
              <span aria-hidden>↻ </span>
              {t("cta_replace")}
            </button>
          </div>
        </div>
      )}

      <Modal
        open={closeOpen}
        title={t("session_close_confirm_title")}
        onClose={() => setCloseOpen(false)}
      >
        <p className="opacity-80">{t("session_close_confirm_body")}</p>
        <div className="mt-5 flex gap-3">
          <Button variant="ghost" fullWidth onClick={() => setCloseOpen(false)}>
            {t("cta_cancel")}
          </Button>
          <Button
            variant="danger"
            fullWidth
            onClick={async () => {
              if (!sess) return;
              const ended = { ...sess, endedAt: Date.now() };
              await putSession(ended);
              // BUG-3 fix: discarding the session must also clear the pack's
              // "in_progress" badge — otherwise the pack list shows a misleading
              // progress bar pointing at a session that no longer exists.
              await setPackProgress({
                packId: sess.packId,
                status: "not_started",
                progressPct: 0,
                cardsSeen: 0,
                totalCards: sess.totalCards,
                lastSessionId: null,
                updatedAt: Date.now(),
              });
              nav("/modes", { replace: true });
            }}
          >
            {t("session_close_discard")}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function ProgressBar({ value }: { value: number }) {
  const pct = Math.max(0, Math.min(1, value)) * 100;
  return (
    <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden mb-1">
      <div
        className="h-full"
        style={{
          width: `${pct}%`,
          background: "linear-gradient(90deg,var(--accent,#fff) 0%, #fff 100%)",
          transition: "width 220ms ease-out",
          boxShadow: "0 0 12px var(--accent, rgba(255,255,255,0.5))",
        }}
      />
    </div>
  );
}

const PHASE_GLYPH = { warmup: "❋", engagement: "✦", peak: "✺" } as const;

function PhaseBadge({
  phase,
  language,
}: {
  phase: import("../domain/enums").Phase;
  language: import("../domain/enums").Language;
}) {
  return (
    <div
      className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider border border-white/20 bg-white/5"
      title={phaseName(language, phase)}
    >
      <span aria-hidden>{PHASE_GLYPH[phase]}</span>
      <span>{phaseName(language, phase)}</span>
    </div>
  );
}

function PicksTicker({ sess }: { sess: Session }) {
  // Show the last 5 player picks (incl. current). Skip REPLACE events because
  // those keep the same player; only DONE/FAILED/SKIPPED rotate the spotlight.
  // Skips are marked publicly (game-theory fix) — an amber ↷ sticks to the
  // name of everyone who dodged, so the dodge is on the record.
  const recentEvents = sess.events.filter((e) => e.action !== "replaced");
  const recent = recentEvents
    .slice(-4)
    .map((e) => ({ id: e.playerId, skipped: e.action === "skipped" }));
  const trail = [...recent, { id: sess.currentPlayerId, skipped: false }].filter(
    (x): x is { id: string; skipped: boolean } => !!x.id
  );
  if (trail.length <= 1) return null;
  const playerName = (id: string) =>
    sess.players.find((p) => p.id === id)?.name ?? "?";
  return (
    <div className="mt-2 flex items-center gap-1.5 text-[11px] opacity-50 truncate">
      {trail.map((x, i) => (
        <span key={i} className="flex items-center gap-1.5 shrink-0">
          {i > 0 && <span aria-hidden>›</span>}
          <span
            className={
              i === trail.length - 1
                ? "font-semibold text-white opacity-100"
                : ""
            }
          >
            {playerName(x.id)}
            {x.skipped && (
              <span className="text-amber-300 opacity-100" aria-label="пропуск">
                {" "}↷
              </span>
            )}
          </span>
        </span>
      ))}
    </div>
  );
}
